import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAdminConventions,
  fetchAdminConventionsMeta,
  type AdminConvention,
  type AdminConventionMeta,
} from "../api/adminApi";
import { getAuthToken } from "../auth";
import { conventions as mockConventions } from "../data";
import type { Convention } from "../types";

function mapApiConvention(row: AdminConvention): Convention {
  const allowedTypes: Convention["type"][] = [
    "forfaitaire",
    "tiers_payant",
    "remboursement",
    "mixte",
  ];
  const type = allowedTypes.includes(row.type as Convention["type"])
    ? (row.type as Convention["type"])
    : "forfaitaire";

  const allowedStatuses: Convention["status"][] = [
    "active",
    "expiring",
    "expired",
    "draft",
    "suspended",
    "cancelled",
  ];
  const status = allowedStatuses.includes(row.status as Convention["status"])
    ? (row.status as Convention["status"])
    : "draft";

  return {
    id: String(row.id),
    code: row.code,
    name: row.name,
    company: row.company || "—",
    companyId: String(row.company_id || ""),
    providerCount: row.provider_count,
    providerIds: (row.provider_ids ?? []).map(String),
    type,
    validityStart: row.validity_start,
    validityEnd: row.validity_end,
    budget: row.budget,
    consumptionPct: row.consumption_pct,
    status,
  };
}

export function useAdminConventions() {
  const [conventions, setConventions] = useState<Convention[]>(mockConventions);
  const [meta, setMeta] = useState<AdminConventionMeta>({ companies: [], providers: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setConventions(mockConventions);
      setMeta({ companies: [], providers: [] });
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const [list, formMeta] = await Promise.all([
        fetchAdminConventions(),
        fetchAdminConventionsMeta(),
      ]);
      setConventions(list.conventions.map(mapApiConvention));
      setMeta(formMeta);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les conventions.");
      setConventions(mockConventions);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const companyFilterOptions = useMemo(
    () => [
      { value: "", label: "Toutes les entreprises" },
      ...meta.companies.map((company) => ({
        value: String(company.id),
        label: company.name,
      })),
    ],
    [meta.companies],
  );

  const providerFilterOptions = useMemo(
    () => [
      { value: "", label: "Tous les prestataires" },
      ...meta.providers.map((provider) => ({
        value: String(provider.id),
        label: provider.city
          ? `${provider.commercial_name} — ${provider.city}`
          : provider.commercial_name,
      })),
    ],
    [meta.providers],
  );

  return {
    conventions,
    meta,
    loading,
    error,
    usingApi,
    companyFilterOptions,
    providerFilterOptions,
    reload: load,
  };
}
